/**
 * Typed marketing-API client.
 *
 * Mirrors the FastAPI response schemas in
 *   hooten-young-marketing/src/hy_marketing/api/routes/*.py
 *
 * All marketing endpoints live under /api/marketing/* and are served by the
 * hy-marketing-api-dev Cloud Run service. Set VITE_MARKETING_API_URL to the
 * deployed URL in prod; leave empty to use the Vite dev proxy.
 */

import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { apiGet } from './client';

// -------------------- Competitor Watch --------------------

export interface CompetitorRow {
  handle: string;
  category: string;
  is_hy: boolean;
  followers: number | null;
  followers_growth_pct: number | null;
  /** Every post we hold for the account, all time — what the profile shows. */
  total_posts: number | null;
  /** Posts inside the engagement window; the denominator behind avg_engagement_per_post. */
  posts_in_window: number | null;
  avg_engagement_per_post: number | null;
  avg_engagement_rate: number | null;
  gap_vs_hy_pct: number | null;
}

export interface CompetitorWatchSummary {
  n_peers: number;
  hy_avg_engagement: number | null;
  peer_median_engagement: number | null;
  category_leader_handle: string | null;
  category_leader_engagement: number | null;
}

export interface CompetitorWatchResponse {
  /** Wall clock at request time. NOT the age of the data — see `data_through`. */
  generated_at: string;
  /** Last day the metrics actually describe (newest snapshot `period_end`). */
  data_through: string | null;
  /** When that snapshot was loaded into the DB. */
  data_ingested_at: string | null;
  /** Where the numbers came from, e.g. "social_insider". */
  data_source: string | null;
  hy_handle: string;
  rows: CompetitorRow[];
  summary: CompetitorWatchSummary;
}

export type CompetitorCategory = 'whiskey' | 'cigar' | 'lifestyle' | 'all';

export function useCompetitorWatch(
  category: CompetitorCategory = 'all',
): UseQueryResult<CompetitorWatchResponse> {
  return useQuery({
    queryKey: ['marketing', 'competitor-watch', category],
    queryFn: () =>
      apiGet<CompetitorWatchResponse>('marketing', '/api/marketing/competitor-watch', {
        category,
      }),
  });
}

// -------------------- Social Trends --------------------

export interface CategoryEngagement {
  category: string;
  posts: number;
  avg_engagement: number;
  avg_likes: number;
  avg_comments: number;
}

export interface WeeklySeriesPoint {
  week_start: string;
  category: string;
  posts: number;
  avg_engagement: number;
}

export interface SocialTrendsResponse {
  generated_at: string;
  window_days: number;
  category_summary: CategoryEngagement[];
  weekly_series: WeeklySeriesPoint[];
}

export function useSocialTrends(windowDays = 365): UseQueryResult<SocialTrendsResponse> {
  return useQuery({
    queryKey: ['marketing', 'social-trends', windowDays],
    queryFn: () =>
      apiGet<SocialTrendsResponse>('marketing', '/api/marketing/social-trends', {
        window_days: windowDays,
      }),
  });
}

// -------------------- Content Performance --------------------

export interface TopPost {
  handle: string;
  category: string | null;
  post_type: string | null;
  posted_on: string | null;
  likes: number | null;
  comments: number | null;
  url: string | null;
  caption_preview: string | null;
  engagement: number;
}

export interface FormatPerformance {
  category: string;
  post_type: string;
  posts: number;
  avg_engagement: number;
}

export interface CaptionBucket {
  category: string;
  bucket: string;
  posts: number;
  avg_engagement: number;
}

export interface ContentPerformanceResponse {
  generated_at: string;
  top_posts: TopPost[];
  format_performance: FormatPerformance[];
  caption_buckets: CaptionBucket[];
}

export type ContentCategory = 'whiskey' | 'cigar' | 'lifestyle' | 'all';

export function useContentPerformance(
  category: ContentCategory = 'all',
  limit = 10,
): UseQueryResult<ContentPerformanceResponse> {
  return useQuery({
    queryKey: ['marketing', 'content-performance', category, limit],
    queryFn: () =>
      apiGet<ContentPerformanceResponse>('marketing', '/api/marketing/content-performance', {
        category,
        limit_top_posts: limit,
      }),
  });
}

// -------------------- Audience Signals --------------------

export interface BestDayByCategory {
  category: string;
  top_day: string;
  top_day_share: number;
  distribution: Record<string, number>;
  n_brands: number;
}

export interface PostTypeMix {
  category: string;
  image: number;
  reel: number;
  carousel: number;
  other: number;
}

export interface GrowthLeader {
  handle: string;
  category: string;
  followers: number | null;
  followers_growth_pct: number | null;
}

export interface AudienceSignalsResponse {
  generated_at: string;
  best_day_by_category: BestDayByCategory[];
  post_type_mix: PostTypeMix[];
  growth_leaders: GrowthLeader[];
  hy_growth: GrowthLeader | null;
}

export function useAudienceSignals(): UseQueryResult<AudienceSignalsResponse> {
  return useQuery({
    queryKey: ['marketing', 'audience-signals'],
    queryFn: () => apiGet<AudienceSignalsResponse>('marketing', '/api/marketing/audience-signals'),
  });
}

// -------------------- Industry News --------------------

export interface NewsStory {
  headline: string;
  url: string;
  why_it_matters: string;
  source: string;
}

export interface IndustryNewsResponse {
  generated_at: string;
  article_count: number;
  headline_takeaway: string;
  stories: NewsStory[];
  error: string | null;
}

export function useIndustryNews(): UseQueryResult<IndustryNewsResponse> {
  return useQuery({
    queryKey: ['marketing', 'industry-news'],
    queryFn: () => apiGet<IndustryNewsResponse>('marketing', '/api/marketing/industry-news/'),
    // Digest is cached server-side (1h); keep it fresh-ish without refetching
    // on every focus since it's a slow upstream call.
    staleTime: 30 * 60 * 1000,
  });
}

// Mark Brown's Industry News Update (INU) — the emailed newsletter, parsed into
// its curated stories. Shown as a distinct section alongside the RSS digest.
export interface InuStory {
  headline: string;
  summary: string | null;
  source: string | null;
  url: string | null;
  date: string | null;
}

export interface InuDigestResponse {
  received_at: string | null;
  story_count: number;
  stories: InuStory[];
}

export function useInuDigest(): UseQueryResult<InuDigestResponse> {
  return useQuery({
    queryKey: ['marketing', 'industry-news', 'inu'],
    queryFn: () => apiGet<InuDigestResponse>('marketing', '/api/marketing/industry-news/inu'),
    staleTime: 30 * 60 * 1000,
  });
}

// -------------------- Blog (Toast & Light) --------------------

export interface BlogDraft {
  generated_at: string;
  title: string;
  excerpt: string;
  category: string;
  body_markdown: string;
  featured_image_concept: string;
  featured_image_url: string | null;
  tags: string[];
  error: string | null;
  /** Review stage — set when drafted via the queue (draft_idea). A 'block'
   *  verdict is auto-rejected server-side; otherwise the draft is in_review. */
  status?: string | null;
  compliance?: ComplianceReview | null;
}

export interface GenerateBlogRequest {
  topic?: string;
  use_news?: boolean;
}

export function useGenerateBlogDraft() {
  return useMutation({
    mutationFn: (req: GenerateBlogRequest) =>
      postJSON<BlogDraft>('/api/marketing/blog/generate', req),
  });
}

// -------------------- Blog ideation ("Suggest topics") --------------------

/** One proposed blog topic. Mirrors blog.py TopicIdea / blog_ideation row. */
export interface TopicIdea {
  id: number | null;
  title: string;
  angle: string | null;
  why_now: string | null;
  source: string | null;
  score: number | null;
  topic_key: string | null;
}

export interface IdeateResponse {
  generated_at: string;
  count: number;
  topics: TopicIdea[];
  error: string | null;
}

export interface IdeateRequest {
  count?: number;
  use_news?: boolean;
}

/** A saved article/idea across its lifecycle. Mirrors blog.py ArticleSummary. */
export interface ArticleSummary {
  id: number;
  created_at: string;
  status: string;
  title: string;
  angle: string | null;
  why_now: string | null;
  source: string | null;
  score: number | null;
  compliance_verdict?: string | null;
}

export interface ReviewActionResponse {
  id: number;
  status: string;
}

/** POST /blog/ideate — propose a ranked queue of topics (default 5, floor 3). */
export function useSuggestTopics() {
  return useMutation({
    mutationFn: (req: IdeateRequest = {}) =>
      postJSON<IdeateResponse>('/api/marketing/blog/ideate', req),
  });
}

/** GET /blog/ideas — read the saved idea/article queue, newest first. */
export function useBlogIdeas(status?: string): UseQueryResult<ArticleSummary[]> {
  return useQuery({
    queryKey: ['marketing', 'blog-ideas', status ?? 'all'],
    queryFn: () =>
      apiGet<ArticleSummary[]>(
        'marketing',
        '/api/marketing/blog/ideas',
        status ? { status } : undefined,
      ),
  });
}

/** POST /blog/ideas/{id}/draft — draft a chosen idea; returns the article. */
export function useDraftIdea() {
  return useMutation({
    mutationFn: (ideaId: number) =>
      postJSON<BlogDraft>(`/api/marketing/blog/ideas/${ideaId}/draft`, {}),
  });
}

/** POST /blog/ideas/{id}/approve — human approves a reviewed draft. */
export function useApproveArticle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      postJSON<ReviewActionResponse>(`/api/marketing/blog/ideas/${id}/approve`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['marketing', 'blog-ideas'] }),
  });
}

/** POST /blog/ideas/{id}/reject — human rejects a reviewed draft. */
export function useRejectArticle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      postJSON<ReviewActionResponse>(`/api/marketing/blog/ideas/${id}/reject`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['marketing', 'blog-ideas'] }),
  });
}

export interface TopicFeedback {
  vote: 'up' | 'down';
  reason?: string;
}

export interface FeedbackResponse {
  id: number;
  feedback: string;
  feedback_reason: string | null;
}

/** POST /blog/ideas/{id}/feedback — thumbs up/down (+reason) on a suggested
 *  topic. Steers future suggestions (feedback-informed prompting). */
export function useTopicFeedback() {
  return useMutation({
    mutationFn: ({ id, ...body }: TopicFeedback & { id: number }) =>
      postJSON<FeedbackResponse>(`/api/marketing/blog/ideas/${id}/feedback`, body),
  });
}

// -------------------- Patterns --------------------

export interface Pattern {
  id: string;
  title: string;
  category: string | null;
  finding: string;
  evidence: Record<string, unknown>;
  confidence: string;
  actionable_for_hy: string;
}

export interface PatternsResponse {
  generated_at: string;
  count: number;
  patterns: Pattern[];
}

export function usePatterns(): UseQueryResult<PatternsResponse> {
  return useQuery({
    queryKey: ['marketing', 'patterns'],
    queryFn: () => apiGet<PatternsResponse>('marketing', '/api/marketing/patterns'),
  });
}

// -------------------- Briefs (AI Content Recommendation) --------------------

export interface FramePrompt {
  shot: number;
  timecode: string;
  duration_s: number;
  prompt: string;
}

export interface SlidePrompt {
  slide: number;
  prompt: string;
}

export interface ContentBrief {
  post_day?: string;
  post_time?: string;
  format?: string;
  theme?: string;
  caption_draft?: string;
  hashtags?: string[];
  /** Video prompt — sequential BEAT structure, used by Generate Reel. */
  visual_direction?: string;
  /** Image prompt — single hero shot, used by Generate Image. Falls back
   *  to visual_direction when not present. */
  image_visual_direction?: string;
  /** Storyboard — one entry per shot with cumulative timecode, duration, and Flux/Veo prompt. */
  frame_prompts?: FramePrompt[];
  /** Carousel slides — one image prompt per slide, used by Generate Carousel. */
  slide_prompts?: SlidePrompt[];
  reasoning?: unknown;
  compliance_notes?: unknown;
  next_publish_date?: string;
  _generated_at?: string;
  _model?: string;
}

export interface ComplianceIssue {
  severity: string;
  quoted?: string;
  rule?: string;
  suggested_rewrite?: string;
}

export interface ComplianceReview {
  verdict: 'compliant' | 'revise' | 'block' | string;
  summary: string;
  issues: ComplianceIssue[];
  state_flags: string[];
  platform_flags: string[];
  notes?: string;
  _reviewed_at?: string;
}

export interface BriefResponse {
  generated_at: string;
  patterns_count: number;
  recommendation: ContentBrief;
  compliance: ComplianceReview;
  revised: boolean;
}

export function useLatestBrief(): UseQueryResult<BriefResponse | null> {
  return useQuery({
    queryKey: ['marketing', 'briefs', 'latest'],
    queryFn: () => apiGet<BriefResponse | null>('marketing', '/api/marketing/briefs/latest'),
  });
}

export function useGenerateBrief() {
  return useMutation({
    mutationFn: async () => {
      // POST — implemented in client below. For now, fetch wrapper inline.
      const base = (import.meta.env.VITE_MARKETING_API_URL ?? '').replace(/\/$/, '');
      const res = await fetch(`${base}/api/marketing/briefs/generate`, { method: 'POST' });
      if (!res.ok) {
        throw new Error(`${res.status} ${res.statusText}`);
      }
      return (await res.json()) as BriefResponse;
    },
  });
}

// -------------------- Brief generation (async job) --------------------
// The pipeline takes ~70s (two sequential Claude calls), so the dashboard
// starts a background job and polls it for stage progress instead of holding
// one request open behind an unexplained spinner.

export interface BriefJobResponse {
  job_id: string;
  status: 'pending' | 'running' | 'done' | 'error';
  /** Pipeline stage currently running, for progress copy in the UI. */
  stage: 'patterns' | 'writing' | 'compliance' | null;
  error: string | null;
  /** The finished brief, present once status === 'done'. */
  brief: BriefResponse | null;
}

export function useGenerateBriefAsync() {
  return useMutation({
    mutationFn: () => postJSON<BriefJobResponse>('/api/marketing/briefs/generate-async', {}),
  });
}

export function useBriefJob(jobId: string | null) {
  return useQuery({
    queryKey: ['marketing', 'brief-job', jobId],
    queryFn: () => apiGet<BriefJobResponse>('marketing', `/api/marketing/briefs/jobs/${jobId}`),
    enabled: jobId !== null,
    // Stages change every ~5-40s; poll faster than the reel job so the
    // progress copy doesn't lag a whole stage behind.
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return 2500;
      return data.status === 'done' || data.status === 'error' ? false : 2500;
    },
  });
}

// -------------------- Media generation (image + video) --------------------

export interface MediaResponse {
  /** Server-assigned id once the asset is persisted to Postgres. */
  id?: number;
  kind: 'image' | 'video';
  url: string;
  provider: string;
  cost_usd: number;
  duration_s: number;
  generated_at: string;
  used_reference?: boolean;
  reference_url?: string;
}

export interface GenerateImageRequest {
  visual_direction: string;
  aspect_ratio?: '1:1' | '9:16' | '16:9' | '4:5';
  /** Auto-pick a HY product photo as the conditioning reference (anchors
   *  bottle/label to HY's actual identity instead of letting Flux invent). */
  use_brand_reference?: boolean;
  /** Or explicitly pass a public URL of a reference image. */
  reference_image_url?: string;
  /** Brief's category — drives which HY product photo (whiskey bottle vs
   *  cigar product). Lifestyle skips the anchor entirely. */
  category?: 'whiskey' | 'cigar' | 'lifestyle';
  /** Composite the REAL HY bottle into a bottle-free generated scene (no
   *  duplicate/invented bottle, no garbled label). Preferred path for whiskey
   *  hero stills; backend gates it to category==='whiskey' + a resolved
   *  reference and otherwise falls back to two-pass. */
  composite?: boolean;
}

export interface GenerateVideoRequest {
  visual_direction: string;
  aspect_ratio?: '9:16' | '16:9' | '1:1';
  duration_sec?: number;
  with_audio?: boolean;
  /** "veo" (default) | "higgsfield" | "kling" */
  video_provider?: 'veo' | 'higgsfield' | 'kling';
  /** Anchor the video to a real HY product photo so the bottle/label matches
   *  the brand instead of being invented (Veo 3.1 referenceImages). */
  use_brand_reference?: boolean;
  /** Drives which HY product photo is used as the reference (whiskey bottle
   *  vs cigar product). */
  category?: 'whiskey' | 'cigar' | 'lifestyle';
}

const MARKETING_BASE = () => (import.meta.env.VITE_MARKETING_API_URL ?? '').replace(/\/$/, '');

async function postJSON<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${MARKETING_BASE()}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`${res.status} ${res.statusText}${text ? ` — ${text.slice(0, 200)}` : ''}`);
  }
  return (await res.json()) as T;
}

export function useGenerateImage() {
  return useMutation({
    mutationFn: (req: GenerateImageRequest) =>
      postJSON<MediaResponse>('/api/marketing/media/image', req),
  });
}

export interface GenerateCarouselRequest {
  slide_prompts: string[];
  aspect_ratio?: '1:1' | '4:5';
  use_brand_reference?: boolean;
  category?: 'whiskey' | 'cigar' | 'lifestyle';
}

export interface CarouselResponse {
  slides: MediaResponse[];
}

/** Generate one anchored HY image per slide prompt (a carousel). */
export function useGenerateCarousel() {
  return useMutation({
    mutationFn: (req: GenerateCarouselRequest) =>
      postJSON<CarouselResponse>('/api/marketing/media/carousel', req),
  });
}

export function useGenerateVideo() {
  return useMutation({
    mutationFn: (req: GenerateVideoRequest) =>
      postJSON<MediaResponse>('/api/marketing/media/video', req),
  });
}

export interface GenerateReelRequest {
  visual_direction: string;
  /** Number of 8-second clips to stitch together (1–4). Default 1 → 8s reel. */
  n_clips?: number;
  aspect_ratio?: '9:16' | '16:9';
  with_audio?: boolean;
  /** Anchor every beat to a real HY product photo so the bottle holds across
   *  the stitched reel instead of being invented per clip. */
  use_brand_reference?: boolean;
  category?: 'whiskey' | 'cigar' | 'lifestyle';
}

export interface ReelJobResponse {
  job_id: string;
  status: 'pending' | 'running' | 'done' | 'error';
  url?: string;
  error?: string;
  n_clips: number;
  beats: string[];
}

export function useGenerateReel() {
  return useMutation({
    mutationFn: (req: GenerateReelRequest) =>
      postJSON<ReelJobResponse>('/api/marketing/media/reel', req),
  });
}

export function useReelJob(jobId: string | null) {
  return useQuery({
    queryKey: ['marketing', 'reel-job', jobId],
    queryFn: () => apiGet<ReelJobResponse>('marketing', `/api/marketing/media/reel/${jobId}`),
    enabled: jobId !== null,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return 10000;
      return data.status === 'done' || data.status === 'error' ? false : 10000;
    },
  });
}

// -------------------- Higgsfield clip (async job) --------------------
// DoP image-to-video renders in the background to avoid a synchronous request
// timing out at the gateway (504). Kick off with useGenerateClip(), then poll
// useClipJob(jobId) until status === 'done'.

export interface ClipJobResponse {
  job_id: string;
  status: 'pending' | 'running' | 'done' | 'error';
  url?: string;
  error?: string;
  provider?: string;
  used_reference: boolean;
}

export function useGenerateClip() {
  return useMutation({
    mutationFn: (req: GenerateVideoRequest) =>
      postJSON<ClipJobResponse>('/api/marketing/media/clip', req),
  });
}

export function useClipJob(jobId: string | null) {
  return useQuery({
    queryKey: ['marketing', 'clip-job', jobId],
    queryFn: () => apiGet<ClipJobResponse>('marketing', `/api/marketing/media/clip/${jobId}`),
    enabled: jobId !== null,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return 5000;
      return data.status === 'done' || data.status === 'error' ? false : 5000;
    },
  });
}

// -------------------- Content Patterns --------------------

export interface ContentPatternSummary {
  id: number;
  category: string;
  post_type: string;
  sample_size: number;
  synthesis: string | null;
  created_at: string;
}

export interface ContentPatternDetail extends ContentPatternSummary {
  visual_pattern: Record<string, unknown> | null;
  video_pattern: Record<string, unknown> | null;
  audio_pattern: Record<string, unknown> | null;
  caption_pattern: Record<string, unknown> | null;
  exemplar_post_ids: number[] | null;
}

export interface PatternBriefResponse {
  pattern_id: number;
  category: string;
  post_type: string;
  generated_at: string;
  brief: ContentBrief;
  compliance: ComplianceReview;
  revised: boolean;
}

export function useContentPatterns(): UseQueryResult<ContentPatternSummary[]> {
  return useQuery({
    queryKey: ['marketing', 'content-patterns'],
    queryFn: () => apiGet<ContentPatternSummary[]>('marketing', '/api/marketing/content-patterns/'),
  });
}

export interface SynthesizeResponse {
  synthesized: number;
  patterns: ContentPatternSummary[];
}

/** Re-run the pattern engine across all category×format groups, refreshing the
 *  synthesized insights from the latest data. Slow (~1-2 min: a Gemini call per
 *  group) — gate the UI accordingly. Invalidates the patterns caches on success
 *  so the card shows the fresh insight. */
export function useSynthesizePatterns() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      postJSON<SynthesizeResponse>('/api/marketing/content-patterns/synthesize', {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['marketing', 'content-patterns'] });
      queryClient.invalidateQueries({ queryKey: ['marketing', 'patterns'] });
    },
  });
}

export function useContentPatternDetail(id: number | null): UseQueryResult<ContentPatternDetail> {
  return useQuery({
    queryKey: ['marketing', 'content-patterns', id],
    queryFn: () =>
      apiGet<ContentPatternDetail>('marketing', `/api/marketing/content-patterns/${id}`),
    enabled: id !== null,
  });
}

export function useGenerateBriefFromPattern() {
  return useMutation({
    mutationFn: (patternId: number) =>
      postJSON<PatternBriefResponse>(`/api/marketing/content-patterns/${patternId}/generate`, {}),
  });
}

export interface CustomBriefRequest {
  category: string;
  post_type: string;
  custom_focus?: string;
}

export function useGenerateCustomBrief() {
  return useMutation({
    mutationFn: (req: CustomBriefRequest) =>
      postJSON<PatternBriefResponse>('/api/marketing/content-patterns/generate-custom', req),
  });
}

// -------------------- Visual Parameters (Pattern Builder) --------------------

export interface ParameterValue {
  id: string;
  label: string;
  count: number;
  pct: number;
  avg_eng: number;
}

export interface ParameterField {
  field_id: string;
  field_label: string;
  db_column: string;
  available: boolean;
  total_analyzed: number;
  values: ParameterValue[];
}

export interface ParameterGroup {
  group_id: string;
  group_label: string;
  fields: ParameterField[];
}

export interface VisualParametersResponse {
  category: string;
  post_type: string;
  total_analyzed: number;
  groups: ParameterGroup[];
}

export interface SelectedParam {
  group_id: string;
  field_id: string;
  value_id: string;
  value_label: string;
}

export interface GenerateFromParamsRequest {
  category: string;
  post_type: string;
  selected: SelectedParam[];
  custom_focus?: string;
}

export interface GenerateFromParamsResponse {
  pattern_id: number;
  category: string;
  post_type: string;
  generated_at: string;
  brief: Record<string, unknown>;
  compliance: Record<string, unknown>;
  revised: boolean;
}

export function useVisualParameters(
  category: string,
  post_type: string,
): UseQueryResult<VisualParametersResponse> {
  return useQuery({
    queryKey: ['marketing', 'visual-parameters', category, post_type],
    queryFn: () =>
      apiGet<VisualParametersResponse>('marketing', '/api/marketing/visual-parameters/', {
        category,
        post_type,
      }),
  });
}

export function useGenerateFromParams() {
  return useMutation({
    mutationFn: (req: GenerateFromParamsRequest) =>
      postJSON<GenerateFromParamsResponse>(
        '/api/marketing/visual-parameters/generate-from-params',
        req,
      ),
  });
}

/** Resolve a server-relative URL (e.g. /static/generated/...) to absolute by
 *  prepending the marketing API base. */
export function resolveMediaUrl(url: string): string {
  if (url.startsWith('http')) return url;
  return `${MARKETING_BASE()}${url}`;
}

// -------------------- Media history (server-persisted gallery) --------------------

export interface MediaHistoryResponse {
  count: number;
  assets: MediaResponse[];
}

export function useMediaHistory(): UseQueryResult<MediaHistoryResponse> {
  return useQuery({
    queryKey: ['marketing', 'media-history'],
    queryFn: () =>
      apiGet<MediaHistoryResponse>('marketing', '/api/marketing/media/history', { limit: 24 }),
  });
}

// -------------------- Blog parameters (Build from blog data) --------------------

export interface BlogParamValue {
  id: string;
  label: string;
  count: number;
  pct: number;
  avg_eng: number; // cross-brand frequency score 0-5.0
  cross_brand: number;
}

export interface BlogParamField {
  field_id: string;
  field_label: string;
  db_column: string;
  available: boolean;
  total_analyzed: number;
  values: BlogParamValue[];
}

export interface BlogParamGroup {
  group_id: string;
  group_label: string;
  fields: BlogParamField[];
}

export interface BlogParametersResponse {
  category: string;
  total_analyzed: number;
  groups: BlogParamGroup[];
}

export interface SelectedBlogParam {
  group_id: string;
  field_id: string;
  value_id: string;
  value_label: string;
}

export interface GenerateFromBlogParamsRequest {
  category?: string;
  selected: SelectedBlogParam[];
  custom_focus?: string;
  event?: string;
  use_news?: boolean;
}

export interface GenerateFromBlogParamsResponse {
  generated_at: string;
  title: string;
  excerpt: string;
  category: string;
  body_markdown: string;
  featured_image_concept: string;
  featured_image_url: string | null;
  tags: string[];
  selected_params: SelectedBlogParam[];
  error: string | null;
}

export function useBlogParameters(
  category: string = 'all',
): UseQueryResult<BlogParametersResponse> {
  return useQuery({
    queryKey: ['marketing', 'blog-parameters', category],
    queryFn: () =>
      apiGet<BlogParametersResponse>('marketing', '/api/marketing/blog-parameters/', { category }),
  });
}

export function useGenerateFromBlogParams() {
  return useMutation({
    mutationFn: (req: GenerateFromBlogParamsRequest) =>
      postJSON<GenerateFromBlogParamsResponse>(
        '/api/marketing/blog-parameters/generate-from-blog-params',
        req,
      ),
  });
}
